import { create } from "zustand";
import { jwtDecode } from "jwt-decode";
import { BusinessInfo } from "./useClaimBusinessStore";
// import { MOCK_USER_DATA } from "data/claim-business-mock-results";

export type ClaimedBusiness = {
  uei: string;
  legal_business_name: string;
  cage_codes: string[];
  isNew?: boolean; // Optional property to indicate if the business is newly claimed
};

// Define types
export type User = {
  name: string;
  email: string;
  role: string;
  businesses: ClaimedBusiness[];
  // add other properties
};

type AuthState = {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  error: string | null;
  newClaimedBusinesses: ClaimedBusiness[] | null;

  checkAuth: () => void;
  loginOkta: () => void;
  logoutOkta: () => void;
  updateUserFromToken: (businesses: BusinessInfo[]) => void;
  updateBusinessVerifiedStatus: (uei: string) => void;
  resetAuthentication: () => void;
};

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;

  const parts = value.split(`; ${name}=`);

  if (parts.length === 2) return parts.pop()?.split(";").shift() || null;

  return null;
};

const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  error: null,
  newClaimedBusinesses: null,

  // Check authentication status on app load (based on token param)
  checkAuth: () => {
    const authToken = getCookie("sbs-session.autht");

    if (authToken) {
      try {
        const decodedUser: User = jwtDecode(authToken);

        set({
          isAuthenticated: true,
          isLoading: false,
          user: decodedUser,
          // NOTE: set newClaimedBusinesses from decoded user, in case if user refreshed the participation agreement page.
          // There is a catch it will include all businesses from the user, not just the claimed ones.
          newClaimedBusinesses: decodedUser?.businesses || [],
        });
      } catch (err) {
        set({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error: "Invalid token",
        });
      }
    } else {
      set({
        isAuthenticated: false,
        isLoading: false,
        user: null,
      });
    }
  },

  // Initiate login by redirecting to the server endpoint
  loginOkta: () => {
    window.location.href = "/_api/auth/okta/login";
    // Mock request
    // window.location.href = "http://localhost:3001/login";
  },

  // // Initiate logout by redirecting to the server endpoint
  logoutOkta: async () => {
    const user = get().user;
    const slug = user?.role === "gov_user" ? "fedhub" : "okta";
    let logoutUrl = `/_api/auth/${slug}/logout`;

    window.location.href = logoutUrl;
  },

  updateUserFromToken: (newBusinesses: BusinessInfo[]) => {
    const authToken = getCookie("sbs-session.autht");

    try {
      const decodedUser: User = jwtDecode(authToken);

      // compare and match business in both updatedUserData.business and businesses by uei and return a new array.
      const matchedBusinesses =
        decodedUser.businesses
          ?.filter((userBiz: any) =>
            newBusinesses.some((biz) => biz.uei === userBiz.uei)
          )
          .map((biz) => ({ ...biz, isNew: true })) || [];

      set({
        user: decodedUser,
        newClaimedBusinesses: matchedBusinesses,
      });
    } catch (err) {
      set({
        error: "Invalid token",
      });

      throw new Error("Invalid Token: Failed to decode auth token");
    }
  },

  updateBusinessVerifiedStatus: (uei: string) => {
    set((state) => {
      const updatedBusinesses = state.newClaimedBusinesses.map((biz) => {
        if (biz.uei === uei) {
          return { ...biz, isNew: false };
        }
        return biz;
      });

      return {
        newClaimedBusinesses: updatedBusinesses,
      };
    });
  },

  resetAuthentication: () => {
    set({
      isAuthenticated: false,
      user: null,
      error: null,
      newClaimedBusinesses: null,
    });
  },
}));

export default useAuthStore;
